/**
 * Mock AI metadata.
 *
 * Produces realistic, structured metadata for a selected video so the Add
 * Content AI experience can be built and demoed before the real analysis
 * backend exists. Everything here is fake but shaped exactly like what a real
 * model response is expected to look like (see the normalized result contract
 * in `aiAnalysisService.js`).
 *
 * Design notes:
 *  - Every field carries a `confidence` (0..1). The service drops any field
 *    below a confidence threshold so the UI "leaves it empty instead of
 *    guessing", per product requirements.
 *  - A small pool of sample titles is rotated so repeated runs feel different.
 *  - No randomness that would make snapshot tests flaky beyond the title pool.
 */

const SAMPLE_TITLES = [
  {
    title: "The Last Harvest",
    shortDescription:
      "A drought-stricken farming family fights to save their land before the season ends.",
    longDescription:
      "When a historic drought threatens their century-old family farm, three siblings must set aside old grievances and gamble everything on one final harvest. A sweeping drama about legacy, sacrifice, and the ties that bind us to the land, told across one unforgiving summer.",
    genre: "Drama",
    subgenre: "Family Drama",
    mood: "Reflective",
    tags: ["family", "farming", "drought", "legacy", "rural", "drama"],
    keywords: ["family farm", "drought survival", "sibling reconciliation"],
    categories: ["Drama", "Award Contenders"],
  },
  {
    title: "Neon Circuit",
    shortDescription:
      "An underground racer is pulled into a high-stakes heist across a rain-soaked megacity.",
    longDescription:
      "In a sprawling neon metropolis, a gifted street racer is blackmailed into driving for a crew planning the impossible. As loyalties fracture and the city's syndicates close in, one last run will decide whether she escapes the circuit or becomes part of it forever.",
    genre: "Action",
    subgenre: "Heist Thriller",
    mood: "High-energy",
    tags: ["racing", "heist", "cyberpunk", "thriller", "cars", "action"],
    keywords: ["street racing", "cyberpunk heist", "megacity chase"],
    categories: ["Action", "Trending Now"],
  },
  {
    title: "Quiet Waters",
    shortDescription:
      "A grieving marine biologist discovers something impossible off a remote coastline.",
    longDescription:
      "Retreating to an isolated research station after a personal loss, a marine biologist begins recording sounds from the deep that shouldn't exist. As she's drawn deeper into the mystery, the line between science, grief, and the unknown starts to dissolve.",
    genre: "Mystery",
    subgenre: "Slow-burn Thriller",
    mood: "Atmospheric",
    tags: ["ocean", "mystery", "grief", "science", "isolation", "thriller"],
    keywords: ["marine biology", "deep sea mystery", "isolated research station"],
    categories: ["Mystery & Thriller", "Critically Acclaimed"],
  },
];

const clamp01 = (n) => Math.min(1, Math.max(0, n));

/** Wraps a value with a confidence score. */
const withConfidence = (value, confidence) => ({
  value,
  confidence: clamp01(confidence),
});

/**
 * Best-effort duration in whole minutes derived from whatever the selected
 * video object exposes. Falls back to a plausible mock runtime.
 */
const deriveRuntimeMinutes = (video) => {
  const seconds = Number(
    video?.duration ?? video?.length ?? video?.video_duration
  );
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.max(1, Math.round(seconds / 60));
  }
  return 98; // plausible feature-length fallback
};

/**
 * Builds a full mock metadata payload for a video.
 *
 * @param {object} video       The selected video object (library item or File-ish).
 * @param {object} [options]
 * @param {number} [options.variant] Force a specific sample (0..2); otherwise rotates.
 * @returns {object} Raw mock result consumed by `normalizeMetadataResult`.
 */
export const generateMockMetadata = (video, options = {}) => {
  const variant =
    typeof options.variant === "number"
      ? options.variant % SAMPLE_TITLES.length
      : Math.floor(Date.now() / 1000) % SAMPLE_TITLES.length;

  const sample = SAMPLE_TITLES[variant];
  const runtime = deriveRuntimeMinutes(video);
  const currentYear = new Date().getFullYear();

  return {
    basic: {
      title: withConfidence(sample.title, 0.94),
      shortDescription: withConfidence(sample.shortDescription, 0.9),
      longDescription: withConfidence(sample.longDescription, 0.88),
      genre: withConfidence(sample.genre, 0.86),
      subgenre: withConfidence(sample.subgenre, 0.72),
      // Language code kept aligned with the form's language <select> values.
      language: withConfidence({ code: "en", name: "English" }, 0.97),
      mood: withConfidence(sample.mood, 0.7),
      ageRating: withConfidence("PG-13", 0.68),
      // Deliberately low confidence — the service will usually drop this so the
      // UI demonstrates "leave empty when unsure".
      contentWarnings: withConfidence(["Mild Language"], 0.41),
      year: withConfidence(String(currentYear), 0.6),
    },
    discovery: {
      tags: withConfidence(sample.tags, 0.82),
      keywords: withConfidence(sample.keywords, 0.8),
      searchMetadata: withConfidence(
        `${sample.title} ${sample.genre} ${sample.tags.join(" ")}`,
        0.75
      ),
      seoTitle: withConfidence(`${sample.title} (${currentYear}) — Watch Now`, 0.85),
      seoDescription: withConfidence(
        `Stream ${sample.title}. ${sample.shortDescription}`,
        0.83
      ),
    },
    media: {
      // Artwork the form has dedicated slots for — real analysis would return
      // extracted stills / generated key art as CDN keys.
      poster: withConfidence(
        "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=480&h=720&fit=crop",
        0.82
      ),
      backdrop: withConfidence(
        "https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=1280&h=720&fit=crop",
        0.8
      ),
      logo: withConfidence(
        "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=480&fit=crop",
        0.64
      ),
      // A short, muted preview loop for the hero slot (sample MP4).
      heroClip: withConfidence(
        "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4",
        0.71
      ),
      // Additional extracted frames (used for the trailer thumbnail, etc.).
      thumbnails: withConfidence(
        [
          "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=400",
          "https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=400",
          "https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?w=400",
        ],
        0.78
      ),
      posterFrame: withConfidence(
        "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=400",
        0.74
      ),
      featuredImages: withConfidence(
        [
          "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=600",
          "https://images.unsplash.com/photo-1594909122845-11baa439b7bf?w=600",
        ],
        0.7
      ),
    },

    // A suggested trailer assembled from the most engaging segment.
    trailer: withConfidence(
      {
        title: `${sample.title} — Official Trailer`,
        type: "TRAILER",
        description: `Official trailer for ${sample.title}. ${sample.shortDescription}`,
        duration: 92,
        url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBigBuckBunny.mp4",
        thumbnail:
          "https://images.unsplash.com/photo-1517604931442-7e0c8ed2963c?w=640&h=360&fit=crop",
      },
      0.7
    ),

    // Detected audio tracks (language + codec + channel layout).
    audioTracks: withConfidence(
      [
        {
          language: "English",
          languageCode: "en",
          codec: "AAC",
          channels: "5.1",
          label: "Original",
        },
        {
          language: "Spanish",
          languageCode: "es",
          codec: "AAC",
          channels: "2.0",
          label: "Dubbed",
        },
      ],
      0.79
    ),

    // Probed technical metadata from the source file.
    technical: {
      max_quality: withConfidence("4K (2160p)", 0.9),
      frame_rate: withConfidence("24 fps", 0.9),
      aspect_ratio: withConfidence("16:9", 0.93),
      video_codec: withConfidence("H.264 / AVC", 0.85),
      audio_codec: withConfidence("AAC", 0.85),
      bitrate: withConfidence("12 Mbps", 0.7),
      hdr: withConfidence("HDR10", 0.62),
    },
    intelligence: {
      runtime: withConfidence(runtime, 0.99),
      // Series-only signals — low confidence for a standalone upload so they
      // usually get dropped, but present to exercise the mapping.
      episodeNumber: withConfidence(null, 0.2),
      seasonNumber: withConfidence(null, 0.2),
      contentType: withConfidence("Movie", 0.81),
      categories: withConfidence(sample.categories, 0.76),
    },
    model: {
      name: "mock-metadata-v1",
      variant,
      generatedAt: new Date().toISOString(),
    },
  };
};

export default generateMockMetadata;
